import { GameId, PresetId, UserId } from "@pax/api-contract/common";
import type { PresetListing } from "@pax/api-contract/presets/v1alpha1";
import { type FoundAccount, SearchAccountsQuery } from "@pax/api-contract/profiles/v1alpha1";
import { focusHaloFlush, panelMotion, press } from "@pax/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";
import { Bubble, Picker } from "@/features/evals/Bubbles";
import { field, fieldLabel, ghostButton, primaryButton } from "@/features/evals/components";
import { presetListingsQuery } from "@/features/presets/queries";
import { useSettled } from "@/lib/use-settled";
import { useHighlight } from "./charts/highlight";
import { exactMoment, instantFrom, localInputValue } from "./format";
import { playerSearchQuery } from "./queries";

/** The person a user id in the address names, read by their exact id. */
function usePickedPerson(userId: string | undefined): FoundAccount | undefined {
  const { data } = useQuery({ ...playerSearchQuery(userId ?? ""), enabled: !!userId });
  return userId ? data?.find(person => person.userId === userId) : undefined;
}

/** Finds someone by user id, email, or username. The first option, `anyone`, clears the pick. */
export function PersonPicker({
  label,
  anyone,
  userId,
  onChange,
}: {
  label: string;
  anyone: string;
  userId: string | undefined;
  onChange: (userId: string | undefined) => void;
}) {
  const [typed, setTyped] = useState("");
  const query = useSettled(typed.trim().replace(/^@/, ""), 250);
  const searchable = SearchAccountsQuery.safeParse({ q: query }).success;
  const search = useQuery({ ...playerSearchQuery(query), enabled: searchable });
  const picked = usePickedPerson(userId);
  const found = searchable ? (search.data ?? []) : [];
  const people = [
    ...(picked ? [picked] : []),
    ...found.filter(person => person.userId !== picked?.userId),
  ];
  return (
    <Picker
      label={label}
      value={userId ?? ""}
      searchable
      search={typed}
      onSearch={setTyped}
      placeholder="Username, email, or user id"
      options={[
        { value: "", label: anyone },
        ...people.map(person => ({
          value: person.userId,
          label: person.displayName,
          detail: person.username ? `@${person.username} · ${person.email}` : person.email,
        })),
      ]}
      onChange={value => {
        setTyped("");
        onChange(value || undefined);
      }}
    />
  );
}

/** Finds a preset by its title or slug. The first option clears the pick. */
export function PresetPicker({
  presetId,
  onChange,
}: {
  presetId: string | undefined;
  onChange: (presetId: string | undefined) => void;
}) {
  const [typed, setTyped] = useState("");
  const query = useSettled(typed, 250);
  const listings = useInfiniteQuery(presetListingsQuery(query));
  const found = listings.data?.pages[0]?.items ?? [];
  const [lastPicked, setLastPicked] = useState<PresetListing>();
  const picked = presetId
    ? (found.find(listing => listing.id === presetId) ??
      (lastPicked?.id === presetId ? lastPicked : undefined))
    : undefined;
  const presets = [...(picked ? [picked] : []), ...found.filter(item => item.id !== picked?.id)];
  return (
    <Picker
      label={presetId ? "Picked preset" : "Preset"}
      value={presetId ?? ""}
      searchable
      search={typed}
      onSearch={setTyped}
      placeholder="Title or slug"
      options={[
        { value: "", label: "Any preset" },
        ...presets.map(listing => ({
          value: listing.id,
          label: listing.title,
          detail: listing.author.displayName,
        })),
      ]}
      onChange={value => {
        setTyped("");
        setLastPicked(presets.find(listing => listing.id === value));
        onChange(value || undefined);
      }}
    />
  );
}

/**
 * A box for one id, typed or pasted, kept in step with the id in the address. A valid id applies at
 * once, and anything else waits with a quiet hint. Clearing the box clears the filter.
 */
function IdFilter({
  id,
  onChange,
  parse,
  label,
  placeholder,
  hint,
}: {
  id: string | undefined;
  onChange: (id: string | undefined) => void;
  parse: (text: string) => string | undefined;
  label: string;
  placeholder: string;
  hint: string;
}) {
  const [typed, setTyped] = useState(id ?? "");
  const [shown, setShown] = useState(id);
  if (shown !== id) {
    setShown(id);
    setTyped(id ?? "");
  }
  const invalid = typed.trim() !== "" && !parse(typed.trim());
  return (
    <span className="inline-flex items-center gap-2 px-3">
      <input
        aria-label={label}
        placeholder={placeholder}
        value={typed}
        spellCheck={false}
        onChange={event => {
          const next = event.target.value;
          setTyped(next);
          const parsed = parse(next.trim());
          if (next.trim() === "") onChange(undefined);
          else if (parsed && parsed !== id) onChange(parsed);
        }}
        className={`w-80 max-w-[calc(100vw-3rem)] font-mono ${field}`}
      />
      {invalid && <span className={fieldLabel}>{hint}</span>}
    </span>
  );
}

/** One game by its id. Clearing the box clears it. */
export function GameIdFilter({
  gameId,
  onChange,
  placeholder = "Paste a game id",
}: {
  gameId: string | undefined;
  onChange: (gameId: string | undefined) => void;
  placeholder?: string;
}) {
  return (
    <IdFilter
      id={gameId}
      onChange={onChange}
      parse={text => GameId.safeParse(text).data}
      label="Game id"
      placeholder={placeholder}
      hint="Not a game id"
    />
  );
}

/** One person by their user id. Clearing the box clears it. */
export function UserIdFilter({
  userId,
  onChange,
  placeholder,
}: {
  userId: string | undefined;
  onChange: (userId: string | undefined) => void;
  placeholder: string;
}) {
  return (
    <IdFilter
      id={userId}
      onChange={onChange}
      parse={text => UserId.safeParse(text).data}
      label="User id"
      placeholder={placeholder}
      hint="Not a user id"
    />
  );
}

/** Picks a preset by its id, for one the title search does not find. */
export function PresetIdFilter({
  presetId,
  onChange,
}: {
  presetId: string | undefined;
  onChange: (presetId: string | undefined) => void;
}) {
  return (
    <IdFilter
      id={presetId}
      onChange={onChange}
      parse={text => PresetId.safeParse(text).data}
      label="Preset id"
      placeholder="Or paste a preset id"
      hint="Not a preset id"
    />
  );
}

/** Picks the instant to read history back from, in the viewer's local time. `undefined` is now. */
export function InstantPicker({
  at,
  onChange,
}: {
  at: string | undefined;
  onChange: (at: string | undefined) => void;
}) {
  const [typed, setTyped] = useState("");
  const picked = instantFrom(typed);
  return (
    <span className="inline-flex items-center gap-1">
      <Bubble
        label="Jump to a time"
        onOpenChange={open => {
          if (open) setTyped(localInputValue(at ?? new Date().toISOString()));
        }}
        face={
          <>
            <span>{at ? "Change time" : "Jump to a time"}</span>
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </>
        }
      >
        {close => (
          <form
            className="w-72 max-w-[calc(100vw-3rem)] space-y-3 p-3"
            onSubmit={event => {
              event.preventDefault();
              if (!picked) return;
              onChange(picked);
              close();
            }}
          >
            <label className="block">
              <span className={fieldLabel}>Show history before</span>
              <input
                type="datetime-local"
                step={1}
                required
                value={typed}
                onChange={event => setTyped(event.target.value)}
                className={`mt-1.5 ${field}`}
              />
            </label>
            <div className="flex justify-end">
              <button type="submit" className={primaryButton} disabled={!picked}>
                Show
              </button>
            </div>
          </form>
        )}
      </Bubble>
      {at && (
        <button type="button" className={ghostButton} onClick={() => onChange(undefined)}>
          Back to now
        </button>
      )}
    </span>
  );
}

/** How a window of placing times reads on its bubble. */
function windowFace(from: string | undefined, to: string | undefined) {
  if (from && to) return `${exactMoment(from)} – ${exactMoment(to)}`;
  if (from) return `Placed from ${exactMoment(from)}`;
  if (to) return `Placed until ${exactMoment(to)}`;
  return "Any time";
}

/** Picks when holds were placed, from and until, in the viewer's local time. Blank ends are open. */
export function PlacedWindowPicker({
  from,
  to,
  onChange,
}: {
  from: string | undefined;
  to: string | undefined;
  onChange: (window: { from?: string; to?: string }) => void;
}) {
  const [draft, setDraft] = useState({ from: "", to: "" });
  return (
    <span className="inline-flex items-center gap-1">
      <Bubble
        label="When placed"
        onOpenChange={open => {
          if (open) setDraft({ from: localInputValue(from), to: localInputValue(to) });
        }}
        face={
          <>
            <span className="max-w-80 truncate">{windowFace(from, to)}</span>
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </>
        }
      >
        {close => (
          <form
            className="w-72 max-w-[calc(100vw-3rem)] space-y-3 p-3"
            onSubmit={event => {
              event.preventDefault();
              onChange({ from: instantFrom(draft.from), to: instantFrom(draft.to) });
              close();
            }}
          >
            <label className="block">
              <span className={fieldLabel}>Placed from</span>
              <input
                type="datetime-local"
                step={1}
                value={draft.from}
                onChange={event => setDraft({ ...draft, from: event.target.value })}
                className={`mt-1.5 ${field}`}
              />
            </label>
            <label className="block">
              <span className={fieldLabel}>Until</span>
              <input
                type="datetime-local"
                step={1}
                value={draft.to}
                onChange={event => setDraft({ ...draft, to: event.target.value })}
                className={`mt-1.5 ${field}`}
              />
            </label>
            <div className="flex justify-end">
              <button type="submit" className={primaryButton}>
                Show
              </button>
            </div>
          </form>
        )}
      </Bubble>
      {(from || to) && (
        <button type="button" className={ghostButton} onClick={() => onChange({})}>
          Any time
        </button>
      )}
    </span>
  );
}

/** Several options toggled together, such as every account of one kind. */
export interface TagGroup {
  key: string;
  label: string;
  members: readonly string[];
}

function Tag({
  pressed,
  group = false,
  hint,
  members,
  onClick,
  children,
}: {
  pressed: boolean;
  group?: boolean;
  hint?: string;
  /** The series the tag brings forward while hovered or focused. */
  members?: readonly string[];
  onClick: (adding: boolean) => void;
  children: ReactNode;
}) {
  const highlight = useHighlight();
  const bringForward = members ? () => highlight.set(members) : undefined;
  const letGo = members ? () => highlight.set(null) : undefined;
  const look = pressed
    ? "bg-foreground text-background ring-foreground"
    : `text-muted-foreground hover:text-foreground ${group ? "ring-foreground/15" : "bg-foreground/5 ring-transparent"}`;
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={hint}
      onPointerEnter={bringForward}
      onPointerLeave={letGo}
      onFocus={bringForward}
      onBlur={letGo}
      onClick={event => onClick(event.shiftKey)}
      className={`rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ring-1 ring-inset ${panelMotion} ${press} ${focusHaloFlush} ${look}`}
    >
      {children}
    </button>
  );
}

const TAG_HINT = "Shift-click to add or remove";

/**
 * Picked keys where none picked means every option: what `Tags` shows pressed, and what to keep
 * for its change, so picking everything leaves the address clean.
 */
export function everyByDefault(picked: readonly string[], every: readonly string[]) {
  return {
    values: picked.length === 0 ? every : picked,
    keep: (next: string[]) => (next.length === every.length ? undefined : next),
  };
}

/**
 * Tags that pick which options a chart shows. A click shows only that tag's options; a shift-click
 * adds them to the pick or takes them out. Dropping the last one picks every option again. Groups
 * of one, or with the same members as an earlier group, are left out, and no group lights up while
 * every option or the `usual` pick is shown. With `sections`, the section tags stand in a column
 * left of a rule, each with its own options to the right on the same line, and options in no section
 * right of "All".
 */
export function Tags({
  label,
  options,
  groups = [],
  sections,
  usual,
  values,
  onChange,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  groups?: readonly TagGroup[];
  sections?: readonly TagGroup[];
  /** The pick shown when none is chosen, if it is not every option. */
  usual?: readonly string[];
  /** The options shown, every one of them for "All". */
  values: readonly string[];
  onChange: (values: string[]) => void;
}) {
  const allValues = options.map(option => option.value);
  const picked = new Set(values);
  const every = options.map(option => option.value);
  const pickedExactly = (pick: readonly string[]) =>
    pick.length === picked.size && pick.every(value => picked.has(value));
  const groupsQuiet = pickedExactly(every) || (usual !== undefined && pickedExactly(usual));
  const sameMembers = (left: TagGroup, right: TagGroup) =>
    left.members.join("\n") === right.members.join("\n");
  const shownGroups = groups.filter(
    (group, index) =>
      group.members.length > 1 &&
      !groups.slice(0, index).some(earlier => sameMembers(earlier, group)),
  );
  const pick = (members: readonly string[], adding: boolean) => {
    const next = new Set(adding ? values : []);
    const allPicked = adding && members.every(member => next.has(member));
    for (const member of members) {
      if (allPicked) next.delete(member);
      else next.add(member);
    }
    const ordered = every.filter(value => next.has(value));
    onChange(ordered.length === 0 ? allValues : ordered);
  };
  const groupTag = (group: TagGroup) => (
    <Tag
      key={group.key}
      group
      hint={TAG_HINT}
      members={group.members}
      pressed={
        !groupsQuiet &&
        group.members.length > 0 &&
        group.members.every(member => picked.has(member))
      }
      onClick={adding => pick(group.members, adding)}
    >
      {group.label}
    </Tag>
  );
  const optionTag = (option: { value: string; label: string }) => (
    <Tag
      key={option.value}
      hint={TAG_HINT}
      members={[option.value]}
      pressed={picked.has(option.value)}
      onClick={adding => pick([option.value], adding)}
    >
      {option.label}
    </Tag>
  );
  const allTag = (
    <Tag pressed={pickedExactly(allValues)} onClick={() => onChange(allValues)}>
      All
    </Tag>
  );
  if (sections) {
    return (
      <fieldset>
        <legend className="sr-only">{label}</legend>
        <div
          className="grid grid-cols-[max-content_1px_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5"
          style={{ gridTemplateRows: `repeat(${sections.length + 1}, auto)` }}
        >
          <span aria-hidden className="col-start-2 row-span-full row-start-1 bg-foreground/10" />
          <div className="col-start-1 row-start-1 flex">{allTag}</div>
          <div className="col-start-3 row-start-1 flex flex-wrap gap-1">
            {options
              .filter(option => !sections.some(section => section.members.includes(option.value)))
              .map(optionTag)}
          </div>
          {sections.map((section, index) => (
            <Fragment key={section.key}>
              <div className="col-start-1 flex" style={{ gridRowStart: index + 2 }}>
                {groupTag(section)}
              </div>
              <div className="col-start-3 flex flex-wrap gap-1" style={{ gridRowStart: index + 2 }}>
                {options.filter(option => section.members.includes(option.value)).map(optionTag)}
              </div>
            </Fragment>
          ))}
        </div>
      </fieldset>
    );
  }
  return (
    <fieldset className="flex flex-wrap items-center gap-1">
      <legend className="sr-only">{label}</legend>
      {allTag}
      {shownGroups.map(groupTag)}
      <span aria-hidden className="mx-1 h-4 w-px bg-foreground/10" />
      {options.map(optionTag)}
    </fieldset>
  );
}
